import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Button from '../../common/Button';
import styles from './SignIn.module.scss';
import Input from '../../blocks/Input';
import { requiredValidator, isFormValid, emailValidator } from '../../../utils/validators';
import { Tabs, Tab } from 'react-bootstrap';
import history from '../../../utils/history';
import {
  ERROR_TYPE_ACCESS_DENIED,
  ERROR_TYPE_PASSWORD_EXPIRED,
  ERROR_TYPE_TOO_MANY_REQUESTS,
} from '../../../constants/errorTypes';
import { VALIDATE_USER, VALIDATE_OTP } from '../../../constants/auth';

const OTP_KEY = 'otp';
const SIGNIN_TAB = 'signin';
const SIGNUP_TAB = 'signup';

const otpEnabled = process.env.REACT_APP_OTP_ENABLED === 'true';

const validators = [
  {
    key: 'username',
    validator: [requiredValidator, emailValidator],
  },
  {
    key: 'password',
    validator: [requiredValidator],
  },
  {
    key: 'otp',
    validator: [requiredValidator],
  },
];

function SignIn(props) {
  const { isError, error, currentStep, resendEmailOtp, backToSignIn } = props;

  const [form, setForm] = useState({
    username: '',
    password: '',
    otp: '',
  });

  const [errorMessage, setErrorMessage] = useState('');

  const calculatedErrorMessage = useMemo(() => {
    let errorMessage = 'Authentication error';

    switch (error.type) {
      case ERROR_TYPE_ACCESS_DENIED:
        const remainingAttempts = parseInt(error.description, 10);
        if (!isNaN(remainingAttempts)) {
          errorMessage = (
            <>
              Wrong password or email.
              <br />
              {remainingAttempts} remaining attempts.
            </>
          );
        } else {
          errorMessage = 'Wrong password or email';
        }
        break;

      case ERROR_TYPE_TOO_MANY_REQUESTS:
        const failedAttempts = parseInt(error.description, 10);
        if (!isNaN(failedAttempts)) {
          errorMessage = (
            <>
              User is locked due to {failedAttempts} failed attempts.
              <br /> Please{' '}
              <a href="mailto:info@cym.bio" target="_blank" rel="noopener noreferrer">
                contact us
              </a>
              .
            </>
          );
        } else {
          errorMessage = (
            <>
              User is locked due to failed attempts.
              <br /> Please{' '}
              <a href="mailto:info@cym.bio" target="_blank" rel="noopener noreferrer">
                contact us
              </a>
              .
            </>
          );
        }
        break;

      case ERROR_TYPE_PASSWORD_EXPIRED:
        errorMessage = (
          <>
            Password expired. Please{' '}
            <Link to={`/resetPassword?email=${encodeURIComponent(form.username)}`}>Reset your Password</Link>
          </>
        );
        break;

      default:
    }

    return errorMessage;
  }, [error, form.username]);

  const getValidators = () => {
    return currentStep === VALIDATE_USER
      ? validators.filter((item) => item.key !== OTP_KEY)
      : validators.filter((item) => item.key === OTP_KEY);
  };

  useEffect(() => {
    if (isError) {
      setErrorMessage(calculatedErrorMessage);
    } else {
      setErrorMessage(false);
    }
  }, [isError, error, calculatedErrorMessage]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm({
      ...form,
      [name]: value,
    });
    setErrorMessage(false);
  };

  const onSubmit = (e) => {
    const { username, password, otp } = form;

    if (currentStep === VALIDATE_USER) {
      props.signIn({
        username,
        password,
      });
    } else {
      props.verifyEmailOtp(otp);
    }

    e.preventDefault();
  };

  const resetPasswordLink = form.username ? `/resetPassword?email=${form.username}` : '/resetPassword';

  return (
    <>
      <Tabs
        defaultActiveKey={SIGNIN_TAB}
        className={cn(styles.tabs, 'tabs', 'tabs-full-width')}
        onSelect={(tab) => (tab === SIGNUP_TAB ? history.push('/signup') : null)}
      >
        <Tab eventKey={SIGNIN_TAB} title="Sign In"></Tab>
        <Tab eventKey={SIGNUP_TAB} title="Sign Up"></Tab>
      </Tabs>

      <form onSubmit={onSubmit} className={styles.form}>
        {currentStep === VALIDATE_USER ? (
          <>
            <Input
              name="username"
              type="email"
              onChange={handleChange}
              placeholder="Email"
              value={form.username}
              validate={[requiredValidator, emailValidator]}
              autocomplete="username"
              blur
            />
            <Input
              name="password"
              type="password"
              onChange={handleChange}
              placeholder="Password"
              marginTop
              value={form.password}
              validate={[requiredValidator]}
              autocomplete="current-password"
              blur
            />
          </>
        ) : (
          <>
            {form.username && <p className={styles.mfaDescription}>A One Time Password was sent to {form.username}</p>}
            <Input
              name="otp"
              onChange={handleChange}
              placeholder="Enter your Pin number"
              marginTop
              value={form.otp}
              validate={[requiredValidator]}
              autocomplete="off"
              blur
            />
            <p className={styles.mfaExpires}>OTP expires after 10 minutes</p>
          </>
        )}
        <div className={styles.settings}>
          {currentStep === VALIDATE_USER && (
            <>
              <span className={styles.errorMessage}>{errorMessage}</span>
              <Link to={resetPasswordLink} className={styles.forgotPassword}>
                Forgot Password?
              </Link>
            </>
          )}
          {currentStep === VALIDATE_OTP && (
            <>
              <button className={styles.buttonLink} type="button" onClick={backToSignIn}>
                Back
              </button>
              <button className={styles.buttonLink} type="button" onClick={resendEmailOtp}>
                Resend Token
              </button>
            </>
          )}
        </div>

        <div className={styles.submitButton}>
          <Button
            type="submit"
            text={otpEnabled && currentStep === VALIDATE_USER ? 'Receive One Time Password' : 'Sign In'}
            disabled={!isFormValid(form, getValidators())}
          />
        </div>
      </form>
    </>
  );
}

export default SignIn;

SignIn.propTypes = {
  signIn: PropTypes.func.isRequired,
  verifyEmailOtp: PropTypes.func.isRequired,
  resendEmailOtp: PropTypes.func.isRequired,
  backToSignIn: PropTypes.func.isRequired,
  isError: PropTypes.bool.isRequired,
  errorCode: PropTypes.string,
  currentStep: PropTypes.number,
};
